import { defineStore } from "pinia";
import { getAirLcl, getAirLclCount } from "@/lib/api";
import { shipments as shipmentsStore } from "@/store/shipments";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const airLcl = defineStore("airLcl", {
  state: () => ({
    airLoading: false,
    lclLoading: false,
    airError: false,
    lclError: false,
    lclData: [],
    airData: [],
    lclCount: null,
    airCount: null,
    airPageSize: 25,
    airTotalCount: 0,
    airLastPage: 0,
    lclPageSize: 25,
    lclTotalCount: 0,
    lclLastPage: 0,
    airPaginationToken: 1,
    lclPaginationToken: 1
  }),
  actions: {
    async getAirBookings(isExport) {
      try {
        this.airLoading = true;
        let queryParams = {};

        if (isExport) {
          queryParams = {
            departingFromDate: this.getFromDate(shipmentsStore().timeFrame),
            departingToDate: this.getToDate(shipmentsStore().timeFrame),
            sort: "etdPortOfLoading:asc"
          };
        } else {
          queryParams = {
            arrivingFromDate: this.getFromDate(shipmentsStore().timeFrame),
            arrivingToDate: this.getToDate(shipmentsStore().timeFrame),
            sort: "etaPortOfDischarge:asc"
          };
        }
        if (shipmentsStore().refSearch) {
          this.airPaginationToken = 1;
          queryParams = {
            ...queryParams,
            reference: shipmentsStore().refSearch
          };
        }
        queryParams.size = this.airPageSize;
        queryParams.page = this.airPaginationToken;
        const response = await getAirLcl("AIR", { ...queryParams });

        this.airData = response.data.map((item, index) => {
          return {
            id: index,
            ...item
          };
        });

        this.airTotalCount = Number(response.headers["total-count"]);
        this.airLastPage = Math.ceil(this.airTotalCount / this.airPageSize);
      } catch (e) {
        if (e?.status !== 404) {
          this.airError = true;
        } else {
          this.airData = [];
        }
      } finally {
        this.airLoading = false;
      }
    },
    getFromDate(timeFrame) {
      switch (timeFrame) {
        case "last_3_days":
          return dayjs().utc().subtract(3, "days").format("YYYY-MM-DD");
        case "last_7_days":
          return dayjs().utc().subtract(7, "days").format("YYYY-MM-DD");
        case "last_month":
          return dayjs().utc().subtract(1, "month").format("YYYY-MM-DD");
        case "last_3_months":
          return dayjs().utc().subtract(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    getToDate(timeFrame) {
      switch (timeFrame) {
        case "next_3_days":
          return dayjs().utc().add(3, "days").format("YYYY-MM-DD");
        case "next_7_days":
          return dayjs().utc().add(7, "days").format("YYYY-MM-DD");
        case "next_month":
          return dayjs().utc().add(1, "month").format("YYYY-MM-DD");
        case "next_3_months":
          return dayjs().utc().add(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    async getLclBookings(isExport, status) {
      try {
        this.lclLoading = true;
        this.lclData = [];
        let queryParams = {};

        if (status === "confirmed") {
          if (isExport) {
            queryParams = {
              departingFromDate: this.getFromDate(shipmentsStore().timeFrame),
              departingToDate: this.getToDate(shipmentsStore().timeFrame),
              sort: "etdPortOfLoading:asc",
              status
            };
          } else {
            queryParams = {
              arrivingFromDate: this.getFromDate(shipmentsStore().timeFrame),
              arrivingToDate: this.getToDate(shipmentsStore().timeFrame),
              sort: "etaPortOfDischarge:asc",
              status
            };
          }
        } else {
          queryParams = {
            status
          };
        }

        if (shipmentsStore().refSearch) {
          this.lclPaginationToken = 1;
          queryParams = {
            ...queryParams,
            reference: shipmentsStore().refSearch
          };
        }
        queryParams.size = this.airPageSize;
        queryParams.page = this.lclPaginationToken;
        const response = await getAirLcl("LCL", { ...queryParams, size: this.lclPageSize });

        this.lclData = response.data.map((item, index) => {
          // Remove when LCL data is fixed
          const portOfLoadingLocalTime = new Date().toLocaleString("en-US", {
            timeZone: item?.portOfLoading?.timeZone?.timeZoneId
          });
          const portOfDischargeLocalTime = new Date().toLocaleString("en-US", {
            timeZone: item?.portOfLoading?.timeZone?.timeZoneId
          });
          if (
            item?.portOfLoading?.departure?.dateTime > portOfLoadingLocalTime &&
            item?.portOfLoading?.departure?.timeQualifier === "ACTUAL"
          ) {
            this.$sendGAEvent({
              category: "SO_LCLInfo",
              event: "DQCS",
              action: "Click",
              label: {
                data: "SOEventActualAndInFuture",
                tpDocNum: item?.tpDocNum,
                direction: "port of loading",
                date: item?.portOfLoading?.departure?.dateTime
              }
            });
          }
          if (
            item?.portOfDischarge?.arrival?.dateTime > portOfDischargeLocalTime &&
            item?.portOfDischarge?.arrival?.timeQualifier === "ACTUAL"
          ) {
            this.$sendGAEvent({
              category: "SO_LCLInfo",
              event: "DQCS",
              action: "Click",
              label: {
                data: "SOEventActualAndInFuture",
                tpDocNum: item?.tpDocNum,
                direction: "port of discharge",
                date: item?.portOfDischarge?.arrival?.dateTime
              }
            });
          }
          return {
            id: index,
            ...item
          };
        });

        this.lclTotalCount = Number(response.headers["total-count"]);
        this.lclLastPage = Math.ceil(this.lclTotalCount / this.lclPageSize);
      } catch (e) {
        if (e?.status !== 404) {
          this.lclError = true;
        } else {
          this.lclData = [];
        }
      } finally {
        this.lclLoading = false;
      }
    },
    async getAirCount() {
      try {
        const count = await getAirLclCount("AIR");
        if (count || count === 0) {
          this.airCount = count;
        }
      } catch (e) {
        this.airCount = 0;
        if (e?.status !== 404) {
          this.airError = e;
        }
      }
    },
    async getLclCount() {
      try {
        const count = await getAirLclCount("LCL");
        if (count || count === 0) {
          this.lclCount = count;
        }
      } catch (e) {
        this.lclCount = 0;
        if (e?.status !== 404) {
          this.lclError = e;
        }
      }
    },
    updateAirPaginationToken(page) {
      this.airPaginationToken = page;
    },
    updateLclPaginationToken(page) {
      this.lclPaginationToken = page;
    }
  }
});
